import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Calculator, TrendingUp, Info, AlertTriangle, TrendingDown } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell } from 'recharts';

const STORAGE_KEY = 'stock_return_data';

export const StockReturnCalculator = () => {
  const [buyPrice, setBuyPrice] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).buyPrice : 1000;
  });
  
  const [sellPrice, setSellPrice] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).sellPrice : 1500;
  });
  
  const [numberOfShares, setNumberOfShares] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).numberOfShares : 100;
  });
  
  const [holdingPeriod, setHoldingPeriod] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).holdingPeriod : 2;
  });
  
  const [dividendsReceived, setDividendsReceived] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).dividendsReceived : 5000;
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      buyPrice,
      sellPrice,
      numberOfShares,
      holdingPeriod,
      dividendsReceived
    }));
  }, [buyPrice, sellPrice, numberOfShares, holdingPeriod, dividendsReceived]);

  const calculateReturns = () => {
    const buy = parseFloat(buyPrice) || 0;
    const sell = parseFloat(sellPrice) || 0;
    const shares = parseFloat(numberOfShares) || 0;
    const period = parseFloat(holdingPeriod) || 0;
    const dividends = parseFloat(dividendsReceived) || 0;

    const totalInvestment = buy * shares;
    const totalSaleValue = sell * shares;
    const capitalGain = totalSaleValue - totalInvestment;
    const totalReturn = capitalGain + dividends;
    
    const absoluteReturn = totalInvestment > 0 ? (totalReturn / totalInvestment) * 100 : 0;
    const percentageReturn = totalInvestment > 0 ? (capitalGain / totalInvestment) * 100 : 0;
    
    // CAGR calculation
    let cagr = 0;
    if (period > 0 && totalInvestment > 0) {
      const finalValue = totalSaleValue + dividends;
      cagr = (Math.pow(finalValue / totalInvestment, 1 / period) - 1) * 100;
    }

    const profitLoss = totalReturn;
    const isProfitable = profitLoss >= 0;

    return {
      totalInvestment,
      totalSaleValue,
      capitalGain,
      dividends,
      totalReturn,
      absoluteReturn,
      percentageReturn,
      cagr,
      profitLoss,
      isProfitable,
      perShareReturn: shares > 0 ? profitLoss / shares : 0
    };
  };

  const generatePriceMovement = () => {
    const buy = parseFloat(buyPrice) || 0;
    const sell = parseFloat(sellPrice) || 0;
    const period = parseFloat(holdingPeriod) || 0;
    
    const data = [];
    const steps = Math.max(10, period * 4); // Quarterly data points
    const priceChange = sell - buy;
    
    for (let i = 0; i <= steps; i++) {
      const timePoint = (period * i) / steps;
      // Simulate realistic price movement with some volatility
      const basePrice = buy + (priceChange * i) / steps;
      const volatility = (Math.sin(i * 0.5) * (Math.abs(priceChange) * 0.1));
      const price = basePrice + volatility;
      
      data.push({
        time: timePoint.toFixed(1),
        price: Math.round(price * 100) / 100,
        buyPrice: buy,
        sellPrice: i === steps ? sell : null
      });
    }
    
    return data;
  };

  const generateBreakdownData = () => {
    const results = calculateReturns();
    
    return [
      { 
        name: 'Initial Investment', 
        value: Math.abs(results.totalInvestment),
        fill: 'hsl(var(--muted))'
      },
      { 
        name: 'Capital Gains', 
        value: Math.abs(results.capitalGain),
        fill: results.capitalGain >= 0 ? 'hsl(var(--secondary))' : 'hsl(var(--destructive))'
      },
      { 
        name: 'Dividends', 
        value: Math.abs(results.dividends),
        fill: 'hsl(var(--accent))'
      }
    ];
  };

  const generateScenarioAnalysis = () => {
    const buy = parseFloat(buyPrice) || 0;
    const shares = parseFloat(numberOfShares) || 0;
    const period = parseFloat(holdingPeriod) || 0;
    
    const priceChanges = [-20, -10, 0, 10, 20];
    
    return priceChanges.map(change => {
      const newSellPrice = buy * (1 + change / 100);
      const profit = (newSellPrice - buy) * shares;
      const returnPct = (profit / (buy * shares)) * 100;
      
      return {
        scenario: `${change > 0 ? '+' : ''}${change}%`,
        price: newSellPrice.toFixed(2),
        profit: Math.round(profit),
        returnPct: returnPct.toFixed(1)
      };
    });
  };

  const generateHoldingPeriodAnalysis = () => {
    const results = calculateReturns();
    const buy = parseFloat(buyPrice) || 0;
    const sell = parseFloat(sellPrice) || 0;
    const shares = parseFloat(numberOfShares) || 0;
    
    const periods = [0.5, 1, 2, 3, 5];
    
    return periods.map(period => {
      const totalInvestment = buy * shares;
      const totalSaleValue = sell * shares;
      const finalValue = totalSaleValue;
      
      let cagr = 0;
      if (period > 0 && totalInvestment > 0) {
        cagr = (Math.pow(finalValue / totalInvestment, 1 / period) - 1) * 100;
      }
      
      return {
        period: `${period}Y`,
        cagr: cagr.toFixed(2),
        value: Math.round(finalValue)
      };
    });
  };

  const results = calculateReturns();
  const priceMovement = generatePriceMovement();
  const breakdownData = generateBreakdownData();
  const scenarioData = generateScenarioAnalysis();
  const holdingPeriodData = generateHoldingPeriodAnalysis();

  const COLORS = ['hsl(var(--muted))', 'hsl(var(--secondary))', 'hsl(var(--accent))', 'hsl(var(--purple))'];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="min-h-screen bg-gradient-hero">
      <nav className="border-b border-border/40 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              </Link>
              <Separator orientation="vertical" className="h-6" />
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg gradient-primary flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
                <span className="font-display text-lg font-semibold text-foreground">
                  Stock Return Calculator
                </span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="calculator" className="space-y-6">
          <div className="overflow-x-auto scrollbar-hide -mx-4 px-4">
            <TabsList className="inline-flex w-auto min-w-full sm:min-w-0 bg-muted/50 p-1 h-auto">
              <TabsTrigger 
                value="calculator" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Calculator className="w-4 h-4 mr-2" />
                Calculator & Results
              </TabsTrigger>
              <TabsTrigger 
                value="charts" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Visual Analysis
              </TabsTrigger>
              <TabsTrigger 
                value="insights" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Info className="w-4 h-4 mr-2" />
                Insights & Explanation
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="calculator" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">What is Stock Return Analysis?</CardTitle>
                <CardDescription className="text-base leading-relaxed">
                  Stock return analysis helps you evaluate the performance of your equity investments. It calculates your absolute returns, 
                  percentage gains, and annualized returns (CAGR) to give you a complete picture of your investment performance.
                </CardDescription>
              </CardHeader>
            </Card>

            <div className="grid lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-1 border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Input Parameters</CardTitle>
                  <CardDescription>Enter your stock investment details</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="buyPrice" className="text-sm font-medium">
                      Buy Price per Share ($)
                    </Label>
                    <Input
                      id="buyPrice"
                      type="number"
                      value={buyPrice}
                      onChange={(e) => setBuyPrice(e.target.value)}
                      placeholder="1000"
                      step="0.01"
                      className="text-base"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="sellPrice" className="text-sm font-medium">
                      Sell Price per Share ($)
                    </Label>
                    <Input
                      id="sellPrice"
                      type="number"
                      value={sellPrice}
                      onChange={(e) => setSellPrice(e.target.value)}
                      placeholder="1500"
                      step="0.01"
                      className="text-base"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="numberOfShares" className="text-sm font-medium">
                      Number of Shares
                    </Label>
                    <Input
                      id="numberOfShares"
                      type="number"
                      value={numberOfShares}
                      onChange={(e) => setNumberOfShares(e.target.value)}
                      placeholder="100"
                      className="text-base"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="holdingPeriod" className="text-sm font-medium">
                      Holding Period (Years)
                    </Label>
                    <Input
                      id="holdingPeriod"
                      type="number"
                      value={holdingPeriod}
                      onChange={(e) => setHoldingPeriod(e.target.value)}
                      placeholder="2"
                      step="0.1"
                      className="text-base"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="dividendsReceived" className="text-sm font-medium">
                      Total Dividends Received ($)
                    </Label>
                    <Input
                      id="dividendsReceived"
                      type="number"
                      value={dividendsReceived}
                      onChange={(e) => setDividendsReceived(e.target.value)}
                      placeholder="5000"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">Optional: Total dividends during holding period</p>
                  </div>

                  <Button 
                    className="w-full gradient-primary hover:opacity-90 transition-opacity shadow-secondary"
                    onClick={() => setBuyPrice(buyPrice)}
                  >
                    <Calculator className="w-4 h-4 mr-2" />
                    Calculate
                  </Button>
                </CardContent>
              </Card>

              <div className="lg:col-span-2 space-y-6">
                <Card className="border-border/50 shadow-md">
                  <CardHeader>
                    <CardTitle className="font-display">Investment Returns Analysis</CardTitle>
                    <CardDescription>Your stock performance metrics</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className={`p-6 rounded-xl bg-gradient-to-br ${results.isProfitable ? 'from-primary-light' : 'from-destructive/10'} to-card border ${results.isProfitable ? 'border-primary/20' : 'border-destructive/20'}`}>
                        <div className="flex items-center gap-3 mb-2">
                          <div className={`w-10 h-10 rounded-lg ${results.isProfitable ? 'bg-primary' : 'bg-destructive'} flex items-center justify-center`}>
                            {results.isProfitable ? <TrendingUp className="w-5 h-5 text-white" /> : <TrendingDown className="w-5 h-5 text-white" />}
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Absolute Return</p>
                        </div>
                        <p className={`text-3xl font-display font-bold ${results.isProfitable ? 'text-primary' : 'text-destructive'}`}>
                          {results.absoluteReturn.toFixed(2)}%
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {formatCurrency(results.totalReturn)} {results.isProfitable ? 'profit' : 'loss'}
                        </p>
                      </div>

                      <div className={`p-6 rounded-xl bg-gradient-to-br ${results.isProfitable ? 'from-secondary-light' : 'from-destructive/10'} to-card border ${results.isProfitable ? 'border-secondary/20' : 'border-destructive/20'}`}>
                        <div className="flex items-center gap-3 mb-2">
                          <div className={`w-10 h-10 rounded-lg ${results.isProfitable ? 'bg-secondary' : 'bg-destructive'} flex items-center justify-center`}>
                            <Calculator className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Percentage Return</p>
                        </div>
                        <p className={`text-3xl font-display font-bold ${results.isProfitable ? 'text-secondary' : 'text-destructive'}`}>
                          {results.percentageReturn.toFixed(2)}%
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">Capital gains only</p>
                      </div>

                      <div className={`p-6 rounded-xl bg-gradient-to-br ${results.isProfitable ? 'from-accent-light' : 'from-destructive/10'} to-card border ${results.isProfitable ? 'border-accent/20' : 'border-destructive/20'}`}>
                        <div className="flex items-center gap-3 mb-2">
                          <div className={`w-10 h-10 rounded-lg ${results.isProfitable ? 'bg-accent' : 'bg-destructive'} flex items-center justify-center`}>
                            <TrendingUp className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">CAGR</p>
                        </div>
                        <p className={`text-3xl font-display font-bold ${results.isProfitable ? 'text-accent' : 'text-destructive'}`}>
                          {results.cagr.toFixed(2)}%
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">Annualized return</p>
                      </div>

                      <div className={`p-6 rounded-xl bg-gradient-to-br ${results.isProfitable ? 'from-purple-light' : 'from-destructive/10'} to-card border ${results.isProfitable ? 'border-purple/20' : 'border-destructive/20'}`}>
                        <div className="flex items-center gap-3 mb-2">
                          <div className={`w-10 h-10 rounded-lg ${results.isProfitable ? 'bg-purple' : 'bg-destructive'} flex items-center justify-center`}>
                            <Calculator className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Net Profit/Loss</p>
                        </div>
                        <p className={`text-3xl font-display font-bold ${results.isProfitable ? 'text-purple' : 'text-destructive'}`}>
                          {formatCurrency(results.profitLoss)}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          ${results.perShareReturn.toFixed(2)} per share
                        </p>
                      </div>
                    </div>

                    <Separator className="my-6" />

                    <div className="p-6 rounded-xl bg-gradient-chart">
                      <div className="grid sm:grid-cols-3 gap-6">
                        <div>
                          <p className="text-sm font-medium text-muted-foreground mb-2">Total Investment</p>
                          <p className="text-2xl font-display font-bold text-foreground mb-1">
                            {formatCurrency(results.totalInvestment)}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {numberOfShares} shares @ ${buyPrice}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-muted-foreground mb-2">Sale Value</p>
                          <p className="text-2xl font-display font-bold text-foreground mb-1">
                            {formatCurrency(results.totalSaleValue)}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {numberOfShares} shares @ ${sellPrice}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-muted-foreground mb-2">Dividends</p>
                          <p className="text-2xl font-display font-bold text-foreground mb-1">
                            {formatCurrency(results.dividends)}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Over {holdingPeriod} years
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="charts" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Price Movement Over Time</CardTitle>
                <CardDescription className="text-base">
                  Simulated price trajectory during your holding period
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={priceMovement}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis 
                        dataKey="time" 
                        label={{ value: 'Time (Years)', position: 'insideBottom', offset: -5 }}
                        stroke="hsl(var(--muted-foreground))"
                      />
                      <YAxis 
                        label={{ value: 'Price ($)', angle: -90, position: 'insideLeft' }}
                        stroke="hsl(var(--muted-foreground))"
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => `$${value}`}
                      />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="price" 
                        stroke={results.isProfitable ? 'hsl(var(--secondary))' : 'hsl(var(--destructive))'}
                        strokeWidth={3}
                        name="Stock Price"
                        dot={false}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="buyPrice" 
                        stroke="hsl(var(--muted-foreground))" 
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        name="Buy Price"
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Profit vs Loss Visualization</CardTitle>
                  <CardDescription>
                    Breakdown of your investment components
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={breakdownData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          dataKey="name" 
                          stroke="hsl(var(--muted-foreground))"
                          angle={-15}
                          textAnchor="end"
                          height={80}
                        />
                        <YAxis 
                          stroke="hsl(var(--muted-foreground))"
                          tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Bar dataKey="value" name="Amount" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Scenario Analysis</CardTitle>
                  <CardDescription>
                    Returns at different exit prices
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={scenarioData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          dataKey="scenario" 
                          stroke="hsl(var(--muted-foreground))"
                        />
                        <YAxis 
                          stroke="hsl(var(--muted-foreground))"
                          tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Bar dataKey="profit" name="Profit/Loss">
                          {scenarioData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.profit >= 0 ? 'hsl(var(--secondary))' : 'hsl(var(--destructive))'} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Holding Period Impact on CAGR</CardTitle>
                <CardDescription className="text-base">
                  How different holding periods would affect your annualized returns
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[350px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={holdingPeriodData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis 
                        dataKey="period" 
                        stroke="hsl(var(--muted-foreground))"
                        label={{ value: 'Holding Period', position: 'insideBottom', offset: -5 }}
                      />
                      <YAxis 
                        stroke="hsl(var(--muted-foreground))"
                        label={{ value: 'CAGR (%)', angle: -90, position: 'insideLeft' }}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                      />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="cagr" 
                        stroke="hsl(var(--accent))" 
                        strokeWidth={3}
                        name="CAGR (%)"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="insights" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Understanding Stock Returns</CardTitle>
                <CardDescription className="text-base">
                  Key metrics and insights for evaluating your equity investments
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-primary">
                    Difference Between Absolute Return & CAGR
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-4">
                    Your investment shows an absolute return of <strong className="text-primary">{results.absoluteReturn.toFixed(2)}%</strong> and 
                    a CAGR of <strong className="text-primary">{results.cagr.toFixed(2)}%</strong>. Understanding the difference is crucial.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg bg-primary-light border border-primary/20">
                      <h4 className="font-semibold text-foreground mb-2">📊 Absolute Return</h4>
                      <p className="text-sm text-muted-foreground mb-2">
                        Total return over the entire period without considering time.
                      </p>
                      <p className="font-mono text-xs bg-muted p-2 rounded">
                        ({formatCurrency(results.totalSaleValue)} + {formatCurrency(results.dividends)} - {formatCurrency(results.totalInvestment)}) / {formatCurrency(results.totalInvestment)} × 100
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-accent-light border border-accent/20">
                      <h4 className="font-semibold text-foreground mb-2">📈 CAGR (Annualized)</h4>
                      <p className="text-sm text-muted-foreground mb-2">
                        Average annual growth rate, accounting for compounding.
                      </p>
                      <p className="font-mono text-xs bg-muted p-2 rounded">
                        [(Final Value / Initial Value)^(1/Years) - 1] × 100
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 p-4 rounded-lg bg-muted/50">
                    <p className="text-sm">
                      <strong>Why CAGR matters:</strong> CAGR allows you to compare investments with different time periods fairly. 
                      A 50% return in 1 year (CAGR 50%) is better than 50% return in 5 years (CAGR 8.45%).
                    </p>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-secondary">
                    Impact of Holding Period
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-4">
                    You held the stock for <strong className="text-secondary">{holdingPeriod} years</strong>. The holding period significantly 
                    affects both your returns and tax liability.
                  </p>
                  <div className="space-y-3 text-base">
                    <div className="p-4 rounded-lg bg-secondary-light border-l-4 border-secondary">
                      <h4 className="font-semibold text-foreground mb-2">⌛ Short-Term (&lt; 1 year)</h4>
                      <p className="text-muted-foreground text-sm">
                        <strong>Tax:</strong> 20% on gains | <strong>Risk:</strong> High volatility | <strong>Strategy:</strong> Trading, momentum plays
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-accent-light border-l-4 border-accent">
                      <h4 className="font-semibold text-foreground mb-2">🕒 Long-Term (&gt; 1 year)</h4>
                      <p className="text-muted-foreground text-sm">
                        <strong>Tax:</strong> 12.5% on gains above $1.25L | <strong>Risk:</strong> Lower | <strong>Strategy:</strong> Buy & hold, fundamental investing
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 p-4 rounded-lg bg-accent/10 border border-accent/20">
                    <p className="text-sm">
                      <strong>Your Status:</strong> {holdingPeriod >= 1 
                        ? `Long-term holding (✅). Enjoy lower tax rates and potential compounding benefits.` 
                        : `Short-term holding (⚠️). Consider holding longer for tax benefits unless you have a specific exit strategy.`
                      }
                    </p>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-accent">
                    Risk: Volatility and Drawdowns
                  </h3>
                  <div className="space-y-3 text-base text-muted-foreground leading-relaxed">
                    <div className="flex gap-3">
                      <span className="text-accent font-semibold flex-shrink-0">🎯</span>
                      <div>
                        <strong className="text-foreground">Market Volatility:</strong> Stock prices can swing ±20-30% in a year. 
                        Your {formatCurrency(Math.abs(results.capitalGain))} {results.isProfitable ? 'gain' : 'loss'} could have been very different with different market timing.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-accent font-semibold flex-shrink-0">📉</span>
                      <div>
                        <strong className="text-foreground">Drawdown Risk:</strong> Even winning stocks can drop 40-50% during corrections. 
                        Can you stomach seeing your {formatCurrency(results.totalInvestment)} investment temporarily drop to {formatCurrency(results.totalInvestment * 0.6)}?
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-accent font-semibold flex-shrink-0">⚡</span>
                      <div>
                        <strong className="text-foreground">Company-Specific Risk:</strong> Individual stocks carry higher risk than diversified 
                        funds. One bad quarterly result, management issue, or sector headwind can cause significant losses.
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-purple">
                    Suggestion: Long-Term Holding vs Frequent Trading
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-4">
                    Historical data shows long-term holding typically outperforms frequent trading for most investors.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-4 mb-4">
                    <div className="p-4 rounded-lg bg-secondary-light border border-secondary/20">
                      <h4 className="font-semibold text-foreground mb-2">✅ Long-Term Holding Benefits</h4>
                      <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                        <li>Lower tax rates (12.5% vs 20%)</li>
                        <li>Reduced transaction costs</li>
                        <li>Benefit from compounding</li>
                        <li>Less stress and time commitment</li>
                        <li>Ride out market volatility</li>
                      </ul>
                    </div>
                    <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/20">
                      <h4 className="font-semibold text-foreground mb-2">⚠️ Frequent Trading Costs</h4>
                      <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                        <li>Higher tax rates</li>
                        <li>Brokerage fees add up</li>
                        <li>Emotional decision making</li>
                        <li>Timing the market is difficult</li>
                        <li>Miss dividend payments</li>
                      </ul>
                    </div>
                  </div>
                  <div className="p-4 rounded-lg bg-purple-light border-l-4 border-purple">
                    <p className="font-semibold text-foreground mb-2">💡 Key Insight</p>
                    <p className="text-sm text-muted-foreground">
                      Research shows that staying invested for 10+ years in quality companies has historically delivered 12-15% CAGR. 
                      Your current CAGR of {results.cagr.toFixed(2)}% {results.cagr >= 12 ? 'is excellent! Consider holding longer.' : 'suggests either a challenging period or the need to reassess the stock quality.'}
                    </p>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-destructive">
                    Important Considerations
                  </h3>
                  <div className="space-y-3 text-base text-muted-foreground leading-relaxed">
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Dividends Matter:</strong> Your {formatCurrency(results.dividends)} in dividends 
                        contributed {((results.dividends / results.totalReturn) * 100).toFixed(1)}% of total returns. Don't ignore dividend-paying stocks!
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Diversification:</strong> Don't put all eggs in one basket. Even great stocks can 
                        underperform. Aim for 10-15 stocks across different sectors or use mutual funds.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Exit Strategy:</strong> Define your exit criteria before investing. 
                        Are you exiting at a target price, after a time period, or based on fundamentals? Stick to your plan.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Transaction Costs:</strong> This calculator doesn't include brokerage, 
                        STT, and other charges. Factor in ~0.5-1% total transaction cost for buying and selling.
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default StockReturnCalculator;